import React from "react";
import { useState } from "react";
import { Mail, Lock, User, CircleAlert, EyeOff, Eye } from "lucide-react";
import { registerUser, useAppDispatch } from "../../../redux";
import { RoleEnum } from "../../shared/model/role.enum";
import { RegisterErrorsType } from "../../shared/model/AuthInterfaces";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import Spinner from "../../shared/components/Spinner";
import { useNavigate } from "react-router-dom";
import { StatusEnum } from "../../shared/model/status.enum";
import { GlobalService } from "../../shared";
import { useTranslation } from "react-i18next";

const RegisterFormComponent = ({ userType }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        userType: userType === RoleEnum.client ? 2 : 4,
        agreedToTerms: false, // new field
    });
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isLoading = useSelector(selectLoaderValue);
    const [errors, setErrors] = useState<RegisterErrorsType>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validate = () => {
        const newErrors: RegisterErrorsType = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.firstName) newErrors.firstName = t("registerForm.firstNameRequired");
        if (!formData.lastName) newErrors.lastName = t("registerForm.lastNameRequired");
        if (!formData.email) {
            newErrors.email = t("registerForm.emailRequired");
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = t("registerForm.invalidEmail");
        }
        if (!formData.password || formData.password.length < 8)
            newErrors.password = t("registerForm.passwordRequired");
        if (formData.confirmPassword !== formData.password) newErrors.confirmPassword = t("registerForm.passwordsNotMatch");
        if (!formData.agreedToTerms) newErrors.agreedToTerms = t("registerForm.mustAgreeToTerms");
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async e => {
        if (!isLoading) {
            e.preventDefault();
            if (validate()) {
                const resultAction = await dispatch(registerUser(formData));
                const payload: any = resultAction?.payload;
        

                if (payload?.user) {
            

                    const { status }: any = payload.user;
                    // If login is successful and user is inactive (id == 2), redirect
                    if (Number(status.id) === StatusEnum.inactive) {
                        navigate("/auth/confirm-email");
                    }
                }
                // submit to API here
            }
        } else {
            GlobalService.showToast("info", t("registerForm.pleaseWait"), t("registerForm.processingRequest"));
        }
    };
    const handleLogin = () => {
        window.location.href = "/auth/login";
    };
    const handleChange = e => {
        const { name, type, checked, value } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <h2 className="text-xl font-semibold text-center">{t("registerForm.completeRegistration")}</h2>
            <div className="mt-[2px] w-full flex justify-center">
                <div className="mt-[2px] w-[80%] h-[6px] bg-blue-500 rounded-md"></div>
            </div>
            {Object.keys(errors).length > 0 && (
                <div className="w-full my-4 bg-red-100 rounded-md px-3 py-2 flex items-center">
                    <CircleAlert className="text-red-500 w-4 h-4" />
                    <p className="text-red-600 text-sm pl-2">{String(Object.values(errors)[0])}</p>
                </div>
            )}

            {/* First & Last Name */}
            <div className={`flex gap-2 ${Object.keys(errors).length == 0 && "py-3"}`}>
                <div className="space-y-2 w-1/2">
                    <label htmlFor="firstName" className="text-sm font-medium text-gray-700 block">
                        {t("registerForm.firstName")}
                    </label>
                    <div className="relative">
                        <input
                            id="firstName"
                            name="firstName"
                            type="text"
                            placeholder={t("registerForm.firstNamePlaceholder")}
                            value={formData.firstName}
                            onChange={handleChange}
                            className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-4 text-sm 
                                    text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                        />
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    </div>
                </div>
                <div className="space-y-2 w-1/2">
                    <label htmlFor="lastName" className="text-sm font-medium text-gray-700 block">
                        {t("registerForm.lastName")}
                    </label>
                    <div className="relative">
                        <input
                            id="lastName"
                            name="lastName"
                            type="text"
                            placeholder={t("registerForm.lastNamePlaceholder")}
                            value={formData.lastName}
                            onChange={handleChange}
                            className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-4 text-sm 
                                    text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                        />
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    </div>
                </div>
            </div>

            {/* Email */}
            <div className={`space-y-2 ${Object.keys(errors).length > 0 ? "pt-3" : "pt-0"}`}>
                <label htmlFor="email" className="text-sm font-medium text-gray-700 block">
                    {t("registerForm.email")}
                </label>
                <div className="relative">
                    <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder={t("registerForm.emailPlaceholder")}
                        value={formData.email}
                        onChange={handleChange}
                        className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-4 text-sm 
                                    text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
            </div>

            {/* Password */}
            <div className="space-y-2 pt-3">
                <div className="flex items-center justify-between">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700">
                        {t("registerForm.password")}
                    </label>
                </div>
                <div className="relative">
                    <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={t("registerForm.passwordPlaceholder")}
                        value={formData.password}
                        onChange={handleChange}
                        className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-12 text-sm 
                        text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2 pt-3">
                <div className="flex items-center justify-between">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700">
                        {t("registerForm.confirmPassword")}
                    </label>
                </div>
                <div className="relative">
                    <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder={t("registerForm.confirmPasswordPlaceholder")}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-12 text-sm 
                        text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                </div>
            </div>
            <div className="py-3 flex items-center">
                <input
                    type="checkbox"
                    name="agreedToTerms"
                    checked={formData.agreedToTerms}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />

                <p className="pl-2 text-sm text-gray-400">
                    {t("registerForm.agreeToTerms")} <span className="text-blue-600">{t("registerForm.termsOfService")}</span> {t("registerForm.and")}{" "}
                    <span className="text-blue-600">{t("registerForm.privacyPolicy")}</span>
                </p>
            </div>
            {/* Submit */}
            <button
                type="submit"
                className="w-full h-12 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                    transition-colors font-medium flex items-center justify-center gap-2">
                {isLoading ? (
                    <>
                        <Spinner size={5} />
                        {t("registerForm.loading")}
                    </>
                ) : (
                    t("registerForm.registerButton")
                )}{" "}
            </button>
            <p className="text-sm text-gray-600 mt-2 text-center text-xs sm:text-sm">
                {t("registerForm.alreadyHaveAccount")}
                <button onClick={handleLogin} className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm ml-1">
                    {t("registerForm.logIn")}
                </button>
            </p>
        </form>
    );
};

export default RegisterFormComponent;
